import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Typography, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { getDateData } from '#component/dashboard/helper';
import { formatter } from '#component/shipment/helper';
import { getAddressString } from '#component/addressbook/helper';
import { fetchShipmentAndLocation } from '#action/shipment/overview';
import DevicesList from '#component/shipment/overview/devices-list';
import AlertStatus from '#component/shipment/dashboard/alert-status';
import { updateFilterData } from '#action/list-filter';
import { shipmentStatusOpts } from '#component/shipment/dashboard/filter/config';
import DeepLinkButton from './deeplinkButton';
import {
  ShipmentsCnt,
  TabListItemCnt,
  ListItemActionsCnt,
  ListItemLink,
  TabListItem,
  ShipmentDateRange,
  ShipmentUid,
  DestinationWrapper,
  DestinationCnt,
  StyledLaunchIcon,
  WordBreak,
  NoData,
  ShowMoreCnt,
  TextGrey,
  AlertStatusCnt,
  ListItemLinkBtn
} from './styles';
import { usePrevious } from '#hooks';

const ShipmentList = props => {
  const dispatch = useDispatch();
  const history = useHistory();
  const MAX_SHIPMENTS_IN_TAB = 50;
  const {
    data,
    account,
    tab,
    isMobile,
    handleOpenMapDialog,
    clickedDevices = [],
    setClickedDevices = () => {}
  } = props;

  const [selectedShipment, setSelectedShipment] = useState({});
  const previousTab = usePrevious(tab);

  const handleSelectShipment = useCallback(
    async currShipment => {
      const { id } = currShipment;

      if (selectedShipment?.id === id) return;
      setSelectedShipment(currShipment);

      if (isMobile && handleOpenMapDialog) {
        handleOpenMapDialog();
      }

      await dispatch(fetchShipmentAndLocation(id));
    },
    [dispatch, isMobile, selectedShipment, handleOpenMapDialog]
  );

  useEffect(() => {
    if (data?.length && !isMobile && tab !== previousTab) {
      handleSelectShipment(data[0]);
    }
  }, [data, isMobile, tab, previousTab, handleSelectShipment]);

  const navigateToShipmentsList = () => {
    const filterKey = Number(tab);
    const storedFilter = localStorage.getItem('listFilter');
    const currentFilter = storedFilter ? JSON.parse(storedFilter) : {};

    const shipmentStatuses = filterKey === 500 ? [300, 400] : [filterKey];
    const filterValue = shipmentStatusOpts.filter(option =>
      shipmentStatuses.includes(+option.data.value));

    const updatedFilter = {
      ...currentFilter,
      shipment: {
        shipmentStatus: filterValue
      }
    };
    const urlParams = new URLSearchParams({ shipmentStatus: shipmentStatuses });

    dispatch(updateFilterData(filterValue, 'shipmentStatus', 'shipment'));
    localStorage.setItem('listFilter', JSON.stringify(updatedFilter));

    history.push(`/shipment?${urlParams.toString()}`);
  };

  return (
    <ShipmentsCnt disablePadding={true}>
      {!data ? (
        <NoData>
          <Typography variant="h4" gutterBottom>
            No shipments
          </Typography>
        </NoData>
      ) :
        clickedDevices.length ?
          <DevicesList
            account={account}
            devices={clickedDevices}
            closeList={setClickedDevices}
          /> : (
            data.slice(0, MAX_SHIPMENTS_IN_TAB).map(currShipment => {
              const { uid, label, id, delivery = [], project, logs } = currShipment;
              const { shipmentDateRange } = getDateData(delivery, account);
              const origin = getAddressString(delivery[0]?.address);
              const destination = getAddressString(
                delivery[delivery.length - 1]?.address
              );
              return (
                <TabListItemCnt
                  onClick={() => handleSelectShipment(currShipment)}
                  selected={selectedShipment?.id === id}
                  key={id}
                >
                  <AlertStatusCnt>
                    <AlertStatus logs={logs} />
                  </AlertStatusCnt>
                  <ListItemLink>
                    <TabListItem>
                      <ShipmentUid>{formatter(uid)}</ShipmentUid>
                      <WordBreak>{label}</WordBreak>
                      <ShipmentDateRange component={WordBreak}>
                        {shipmentDateRange}
                      </ShipmentDateRange>
                      {project && (
                      <div>
                        <span>Project: </span>
                        <TextGrey>{project?.title}</TextGrey>
                      </div>
                      )}
                      <DestinationWrapper
                        style={{
                          display: `${origin || destination ? 'block' : 'none'}`
                        }}
                      >
                        {origin && (
                        <DestinationCnt>
                          <span>FROM: </span>
                          <TextGrey>{origin}</TextGrey>
                        </DestinationCnt>
                        )}
                        {destination && (
                        <DestinationCnt>
                          <span>TO: </span>
                          <TextGrey>{destination}</TextGrey>
                        </DestinationCnt>
                        )}
                      </DestinationWrapper>
                    </TabListItem>
                  </ListItemLink>
                  <ListItemActionsCnt>
                    <Tooltip title="Open shipment">
                      <ListItemLinkBtn to={`/shipment/${id}`}>
                        <IconButton>
                          <StyledLaunchIcon />
                        </IconButton>
                      </ListItemLinkBtn>
                    </Tooltip>
                    <DeepLinkButton shipmentId={id} />
                  </ListItemActionsCnt>
                </TabListItemCnt>
              );
            })
          )}
      {data && data.length > MAX_SHIPMENTS_IN_TAB && !clickedDevices.length && (
        <ShowMoreCnt>
          <Button onClick={navigateToShipmentsList}>
            {`Show all (${data.length})`}
          </Button>
        </ShowMoreCnt>
      )}
    </ShipmentsCnt>
  );
};

export default ShipmentList;
